'use client';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ChevronDown, Search } from 'lucide-react';
import { useState } from 'react';

interface User {
  user_id: number;
  name: string;
  avatar: string | null;
  time_zone: string | null;
}

interface ServiceStaffProps {
  serviceId: string;
  users: User[];
  selectedStaff: string;
  staffSearch: string;
  onStaffSelect: (serviceId: string, staffId: string) => void;
  onSearchChange: (value: string) => void;
  getFirstAvailable: (userId: string) => string | null;
}

export function ServiceStaff({
  serviceId,
  users,
  selectedStaff,
  staffSearch,
  onStaffSelect,
  onSearchChange,
  getFirstAvailable,
}: ServiceStaffProps) {
  const [open, setOpen] = useState(false);

  const getSelectedStaffMember = () => {
    if (!selectedStaff) {
      return {
        user_id: 0,
        name: 'No Preference',
        avatar: null,
        time_zone: null,
      };
    }
    return users.find((user) => user.user_id.toString() === selectedStaff);
  };

  const filterStaff = (staff: User[], search: string) => {
    return staff.filter((user) =>
      user.name.toLowerCase().includes(search.toLowerCase())
    );
  };

  // Create a handler for staff selection that also closes the popover
  const handleStaffSelect = (serviceId: string, staffId: string) => {
    onStaffSelect(serviceId, staffId);
    setOpen(false); // Close the popover
  };

  const selectedMember = getSelectedStaffMember();
  const firstAvailable = selectedMember
    ? getFirstAvailable(selectedMember.user_id.toString())
    : null;

  return (
    <div>
      <label className="text-sm font-medium mb-4 block">Select Staff</label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="w-full justify-between h-auto py-3"
          >
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                {selectedMember?.avatar ? (
                  <AvatarImage
                    src={selectedMember.avatar}
                    alt={selectedMember.name}
                  />
                ) : (
                  <AvatarFallback>
                    {selectedMember?.name.charAt(0)}
                  </AvatarFallback>
                )}
              </Avatar>
              <div className="text-left">
                <div className="font-medium">{selectedMember?.name}</div>
                <div className="text-sm text-muted-foreground">
                  Next available at {firstAvailable || 'No availability'}
                </div>
              </div>
            </div>
            <ChevronDown className="h-4 w-4 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0" align="start">
          <div className="sticky top-0 bg-background z-10 p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search staff..."
                className="pl-8"
                value={staffSearch}
                onChange={(e) => onSearchChange(e.target.value)}
              />
            </div>
          </div>
          <ScrollArea className="h-72">
            <div className="p-2">
              <div
                className="flex items-center gap-3 p-2 cursor-pointer hover:bg-muted rounded-md"
                onClick={() => handleStaffSelect(serviceId, '')}
              >
                <Avatar className="h-10 w-10">
                  <AvatarFallback>NP</AvatarFallback>
                </Avatar>
                <div className="text-left">
                  <div className="font-medium">No Preference</div>
                  <div className="text-sm text-muted-foreground">
                    Next available at{' '}
                    {getFirstAvailable('') || 'No availability'}
                  </div>
                </div>
              </div>
              {filterStaff(users, staffSearch).map((user) => {
                const userFirstAvailable = getFirstAvailable(
                  user.user_id.toString()
                );
                return (
                  <div
                    key={user.user_id}
                    className="flex items-center gap-3 p-2 cursor-pointer hover:bg-muted rounded-md"
                    onClick={() =>
                      handleStaffSelect(serviceId, user.user_id.toString())
                    }
                  >
                    <Avatar className="h-10 w-10">
                      {user.avatar ? (
                        <AvatarImage src={user.avatar} alt={user.name} />
                      ) : (
                        <AvatarFallback>{user.name.charAt(0)}</AvatarFallback>
                      )}
                    </Avatar>
                    <div className="text-left">
                      <div className="font-medium">{user.name}</div>
                      <div className="text-sm text-muted-foreground">
                        Next available at{' '}
                        {userFirstAvailable || 'No availability'}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </PopoverContent>
      </Popover>
    </div>
  );
}
